<template>
  <div>
    <HrPageLayout
      title="My Performance Appraisals"
      subtitle="Complete assigned appraisal forms and track supervisor feedback"
      icon="📋"
      :breadcrumb-items="[
        { label: 'Dashboard', route: '/employee-dashboard' },
        { label: 'My Appraisals' }
      ]"
    >
      <div class="my-appraisals-content">
        <div class="hr-metrics-grid hr-mb-4">
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bx-file"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Assigned Forms</p>
            <h2 class="hr-metric-value">{{ statistics.total }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-4">
            <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Pending Action</p>
            <h2 class="hr-metric-value">{{ statistics.pending }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Approved</p>
            <h2 class="hr-metric-value">{{ statistics.approved }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-2">
            <div class="hr-metric-icon"><i class="bx bx-revision"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Returned</p>
            <h2 class="hr-metric-value">{{ statistics.returned }}</h2>
            </div>
          </div>
        </div>

        <div v-if="loading" class="hr-loading-wrap hr-py-5">
          <div class="hr-spinner"></div>
          <p class="hr-text-muted hr-mt-2">Loading your appraisal forms...</p>
        </div>

        <div v-else-if="forms.length === 0" class="hr-chart-card hr-text-center hr-py-5">
          <i class="bx bx-file-blank" style="font-size: 48px; color: #64748b;"></i>
          <h5 class="hr-mt-4">No Appraisal Forms Assigned</h5>
          <p class="hr-text-muted">When HR publishes a form for your department, position, or role, it will appear here.</p>
        </div>

        <div v-else class="form-cards">
          <div v-for="form in forms" :key="form.id" class="form-card">
            <div class="form-card__head">
              <div>
                <h3>{{ form.title }}</h3>
                <p class="hr-text-muted">
                  {{ form.appraisalYear }} <span v-if="form.periodLabel">· {{ form.periodLabel }}</span>
                </p>
              </div>
              <span class="hr-badge" :class="getStatusBadgeClass(form.status)">{{ form.status }}</span>
            </div>
            <p v-if="form.description" class="form-card__desc">{{ form.description }}</p>
            <div class="form-card__meta">
              <span v-if="form.submissionDeadline"><i class="bx bx-calendar"></i> Due {{ form.submissionDeadline }}</span>
              <span v-if="form.supervisorName"><i class="bx bx-user"></i> Supervisor: {{ form.supervisorName }}</span>
            </div>
            <div v-if="form.supervisorNotes && form.status === 'Returned'" class="return-note">
              <strong>Supervisor note:</strong> {{ form.supervisorNotes }}
            </div>
            <div class="form-card__actions">
              <button
                v-if="canFill(form)"
                class="hr-btn hr-btn-primary"
                @click="openForm(form)"
              >
                <i class="bx bx-edit"></i>
                {{ form.status === 'Returned' ? 'Revise & Resubmit' : form.status === 'Not Started' ? 'Start Appraisal' : 'Continue' }}
              </button>
              <button
                v-if="form.hasCompletedPdf && form.submissionId"
                class="hr-btn hr-btn-outline"
                @click="viewCompleted(form)"
              >
                <i class="bx bx-show"></i> View Submitted PDF
              </button>
            </div>
          </div>
        </div>
      </div>
    </HrPageLayout>
  </div>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, executeDownload } from "@/api";

export default {
  name: "MyAppraisals",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      forms: [],
      statistics: { total: 0, pending: 0, approved: 0, returned: 0 },
    };
  },
  mounted() {
    this.loadForms();
  },
  methods: {
    async loadForms() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getMyAppraisalPdfForms");
        const res = await execute(data, authUrl);
        this.forms = res?.data?.success ? res.data.data?.forms || [] : [];
        this.calculateStatistics();
      } catch (error) {
        console.error("Failed to load appraisal forms:", error);
        this.$toast?.error("Failed to load appraisal forms") || alert("Failed to load appraisal forms");
      } finally {
        this.loading = false;
      }
    },
    calculateStatistics() {
      const all = this.forms;
      this.statistics.total = all.length;
      this.statistics.approved = all.filter((f) => f.status === "Approved").length;
      this.statistics.returned = all.filter((f) => f.status === "Returned").length;
      this.statistics.pending = all.filter((f) =>
        ["Not Started", "Draft", "Returned", "Submitted"].includes(f.status)
      ).length;
    },
    canFill(form) {
      return ["Not Started", "Draft", "Returned"].includes(form.status);
    },
    openForm(form) {
      this.$router.push({ path: "/employee-appraisal-form", query: { formId: form.id } });
    },
    async viewCompleted(form) {
      try {
        const data = new FormData();
        data.append("function", "downloadAppraisalPdfFile");
        data.append("type", "completed");
        data.append("submissionId", form.submissionId);
        const res = await executeDownload(data, authUrl);
        const blob = new Blob([res.data], { type: "application/pdf" });
        window.open(window.URL.createObjectURL(blob), "_blank");
      } catch (e) {
        this.$toast?.error("Could not open PDF") || alert("Could not open PDF");
      }
    },
    getStatusBadgeClass(status) {
      const map = {
        "Not Started": "hr-badge-secondary",
        Draft: "hr-badge-secondary",
        Submitted: "hr-badge-warning",
        Returned: "hr-badge-info",
        Approved: "hr-badge-success",
        Rejected: "hr-badge-danger",
      };
      return map[status] || "hr-badge-secondary";
    },
  },
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";
.form-cards { display: grid; gap: 16px; }
.form-card {
  background: #fff; border: 1px solid #e2e8f0; border-radius: 16px; padding: 20px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
}
.form-card__head { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
.form-card__head h3 { margin: 0 0 4px; font-size: 18px; }
.form-card__desc { margin: 12px 0 0; color: #475569; }
.form-card__meta { display: flex; flex-wrap: wrap; gap: 16px; margin-top: 12px; font-size: 13px; color: #64748b; }
.form-card__meta i { margin-right: 4px; }
.form-card__actions { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 16px; }
.return-note { margin-top: 12px; padding: 10px 12px; background: #fff7ed; border-radius: 10px; border: 1px solid #fdba74; font-size: 14px; }
</style>
